import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import {
  filter, max, values, slice,
} from 'lodash';
import ordinal from 'ordinal-js';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  getExclusionStats,
  getFeedbackRequestSettings,
} from '../../../Dashboard/DashboardReducer';
import { getHasNoDecisionSetup } from '../../../../components/search-form/search-form-reducer';
import NumberDisplay from '../../../../components/number-display';
import MoneyDisplay from '../../../../components/money-display';

import StatsCell from '../../../Dashboard/components/CurrentStats/StatsCell';
import LabelHeaderRenderer from '../../../Dashboard/components/WinLossStats/LabelHeaderRenderer';
import Clickable from '../../../Dashboard/components/WinLossStats/Clickable';


import styles from '../../../Dashboard/components/CurrentStats/styles.css';
import wrapperStyles from '../../../Dashboard/components/WinLossStats/styles.css';


const WIN_CELL = 'WIN_CELL';
const LOST_CELL = 'LOST_CELL';
const NO_DECISION_CELL = 'NO_DECISION_CELL';
const OPEN_CELL = 'OPEN_CELL';
const PER_PAGE = 3;



function AmountRenderer(props) {
  const { value, data, onSelectOpportunities } = props;
  const comp = (
    <MoneyDisplay value={value} abbreviated />
  );
  return (
    <Clickable
      opportunityIds={data.opportunityIds}
      onSelect={onSelectOpportunities}
    >
      {comp}
    </Clickable>
  );
}

function LabelRenderer(props) {
  const { value, data, cellType, onSelectOpportunities } = props;
  const { isUnknown } = data;
  let comp = (<span>{value}</span>);

  if (!isUnknown) {
    const tooltip = (
      <Tooltip id={`${cellType}-${data.id}-tips`}>{value}</Tooltip>
    );
    comp = (
      <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={tooltip}>
        <span>{value}</span>
      </OverlayTrigger>
    );
  }
  
  return (
    <Clickable
      opportunityIds={data.opportunityIds}
      onSelect={onSelectOpportunities}
    >
      {comp}
    </Clickable>
  );
}

function CountRenderer(props) {
  const { value, data, onSelectOpportunities } = props;
  const { isUnknown } = data;
  const comp = (
    <NumberDisplay value={value} precision={0} />
  );
  return (
    <Clickable
      opportunityIds={data.opportunityIds}
      onSelect={onSelectOpportunities}
    >
      {comp}
    </Clickable>
  );
}

function RankingRenderer(props) {
  const { value, data, onSelectOpportunities } = props;
  const { isUnknown } = data;
  
  if (isUnknown) {
    return null;
  }
  const comp = (
    <div className="text-right">
      {value}<sup>{ordinal.ordinalSuffix(value)}</sup>
    </div>
  );
  return (
    <Clickable
      opportunityIds={data.opportunityIds}
      onSelect={onSelectOpportunities}
    >
      {comp}
    </Clickable>
  );
}
class ExclusionStats extends Component {
  constructor(props) {
    super(props);
    this.state = {
      heights: {
        [WIN_CELL]: 0,
        [LOST_CELL]: 0,
        [NO_DECISION_CELL]: 0,
        [OPEN_CELL]: 0,
      },
      pages: {
        [WIN_CELL]: 1,
        [LOST_CELL]: 1,
        [NO_DECISION_CELL]: 1,
        [OPEN_CELL]: 1,
      },
      cellWidth: 0,
      maxHeight: 0,
    };
    this.handleCellHeightChange = this.handleCellHeightChange.bind(this);
    this.loadPage = this.loadPage.bind(this);
  }

  handleCellHeightChange(type, height) {
    const { heights } = this.state;
    const newHeights = {
      ...heights,
      [type]: Math.ceil(height),
    };
    const maxHeight = max(values(newHeights));
    this.setState({
      heights: newHeights,
      maxHeight,
    });
  }

  loadPage(cellType, deltaPage = 1) {
    const currentPage = this.state.pages[cellType];
    let statType = '';
    if (cellType === WIN_CELL) {
      statType = 'Won';
    } else if (cellType === LOST_CELL) {
      statType = 'Lost';
    } else if (cellType === NO_DECISION_CELL) {
      statType = 'No Decision';
    } else if (cellType === OPEN_CELL) {
      statType = 'Open';
    }
    
    const reasons = this.props.stats[statType]?.reasons || [];
 
    const totalPages = Math.ceil(reasons.length / PER_PAGE);
    let nextPage = currentPage + deltaPage;
    
    if (deltaPage > 0 && nextPage > totalPages) {
        nextPage = 1;
    }
    if (deltaPage < 0 && nextPage <= 0) {
      nextPage = totalPages;
    }
    this.setState({ pages: { ...this.state.pages, [cellType]: nextPage } });
  }


  renderTable(stats, cellType) {
    let currentPage = this.state.pages[cellType];
    const { cellWidth } = this.state;
    const minHeight = 29 * 5;
    const { reasons } = stats;
    const allGoodReasons = filter(reasons, (r) => { return r.type !== 'OTHER' });

    let goodReasons = slice(allGoodReasons, (currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);
    if (allGoodReasons.length > 0 && goodReasons.length === 0) {
      currentPage = 1;
      goodReasons = slice(allGoodReasons, (currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);
      // this.setState({ pages: { ...this.state.pages, [cellType]: 0 } })
    }


    let unknownReasons = slice(filter(reasons, (r) => { return r.type === 'OTHER' }), 0, 1); // just to make sure;
    if (unknownReasons[0]?.count === 0 && allGoodReasons.length <= PER_PAGE) {
      unknownReasons = null;
    }
    
    const countWidth = 90;
    const amountWidth = 100;
    const rankingWidth = 55;
    const totalPage = Math.ceil(allGoodReasons.length / PER_PAGE);


    const columnDefs = [
      {
        headerName: '',
        field: 'order',
        suppressMenu: true,
        width: rankingWidth,
        cellRendererSelector: (params) => {
          if (params?.data?.type === 'OTHER') {
            return {
              component: LabelHeaderRenderer,
              params: {
                isPrevious: false,
                isNext: true,
                currentPage,
                cellType,
                totalPage,
                onSelectPage: this.loadPage,
              },
            };
          }
          return {
            component: RankingRenderer,
          };
        },
        headerComponent: LabelHeaderRenderer,
        headerComponentParams: {
          isPrevious: true,
          isNext: false,
          currentPage,
          cellType,
          totalPage,
          onSelectPage: this.loadPage,
        },
      },
      {
        headerName: 'Reason',
        field: 'label',
        cellRenderer: LabelRenderer,
        suppressMenu: true,
        width: cellWidth - amountWidth - countWidth - rankingWidth - 4,
        cellRendererParams: {
          cellType,
          onSelectOpportunities: this.props.requestShowOpportunities,
        },
      },
      {
        headerName: 'Amount',
        field: 'amount',
        width: amountWidth,
        cellRenderer: AmountRenderer,
        type: 'numericColumn',
        suppressMenu: true,
        cellRendererParams: {
          onSelectOpportunities: this.props.requestShowOpportunities,
        },
      },

      {
        headerName: 'Count',
        field: 'count',
        width: countWidth,
        type: 'numericColumn',
        suppressMenu: true,
        cellRenderer: CountRenderer,
        cellRendererParams: {
          onSelectOpportunities: this.props.requestShowOpportunities,
        },
      },
    ];
    return (
      <div className={`${styles.rankTable} ${wrapperStyles.table} ag-theme-alpine`} style={{ height: minHeight, width: '100%' }} >
        <AgGridReact
          reactNext
          enableColResize
          columnDefs={columnDefs}
          rowData={goodReasons}
          pinnedBottomRowData={unknownReasons}
          getRowClass={this.getRowClass}
        />
      </div>
    );

  }

  renderStats(stats, type) {
    let title = null;
    let cls = null;
    let rateTitle = null;

    if (type === WIN_CELL) {
      title = 'Won';
      rateTitle = 'win rate';
      cls = styles.wonCell;
    } else if (type === LOST_CELL) {
      title = 'Lost';
      rateTitle = 'loss rate';
      cls = styles.lostCell;
    } else if (type === NO_DECISION_CELL) {
      title = 'No Decision';
      rateTitle = 'No decision rate';
      cls = styles.noDecisionCell;
    } else if (type === OPEN_CELL) {
      title = 'Open Deals';
      rateTitle = 'Open deal rate';
      cls = styles.openCell;
    }

    let statsNumberClick = null;
    if (stats.opportunityIds && stats.opportunityIds.length > 0) {
      statsNumberClick = () => {
        this.props.requestShowOpportunities(stats.opportunityIds, type);
      };
    }


    return (
      <StatsCell
        className={cls}
        title={title}
        rateTitle={rateTitle}
        isLoading={false}

        rank={null}
        rankings={[]}
        isRankingLoading={true}

        sumAmount={stats.amount}
        sumCount={stats.count}
        amountRate={stats.percentageAmount * 100}
        countRate={stats.percentageCount * 100}

        sumCountOnClick={statsNumberClick}
        sumAmountOnClick={statsNumberClick}

        minHeight={this.state.maxHeight}
        onHeightChange={(height) => {
          this.handleCellHeightChange(type, height);
        }}
        onWidthChange={(cellWidth) => {
          this.setState({ cellWidth });
        }}
      >
        {this.renderTable(stats, type)}
      </StatsCell>
    );
  }

  render() {
    const { hasNoDecision, feedbackRequestSettings } = this.props;
    const openStages = this.props.stats.Open || {};
    const includeOpenOpportunities = feedbackRequestSettings.include_open_opportunities === true;
    return (
      <div>
        <div className={styles.stats}>
          {this.renderStats(this.props.stats.Won, WIN_CELL)}
          {this.renderStats(this.props.stats.Lost, LOST_CELL)}
          {hasNoDecision && this.renderStats(this.props.stats['No Decision'], NO_DECISION_CELL)}
          {includeOpenOpportunities && openStages && openStages.count > 0 && this.renderStats(openStages, OPEN_CELL)}
        </div>
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    stats: getExclusionStats(state),
    feedbackRequestSettings: getFeedbackRequestSettings(state),
    hasNoDecision: getHasNoDecisionSetup(state),
  };
}

ExclusionStats.propTypes = {
  hasNoDecision: PropTypes.bool,
  feedbackRequestSettings: PropTypes.shape({
    include_open_opportunities: PropTypes.bool,
  }),
  requestShowOpportunities: PropTypes.func,
};

ExclusionStats.defaultProps = {
  hasNoDecision: false,
  requestShowOpportunities: () => {},
};

ExclusionStats.contextTypes = {
};
export default connect(mapStateToProps)(ExclusionStats);
